import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axiosInstance from 'axiosConfig';
import { queryClient } from 'App';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { useSearchParams } from "react-router-dom";
import { mutateWithOffline } from 'utils/mutateWithOffline';
import { setOfflineFileBlob } from 'hooks/useOfflineFileBlobs';
import { resolveOfflineUuid } from 'hooks/useOfflineUuidMap';
import {
  getOfflineMeasurementInfo,
  upsertOfflineMeasurementInfo,
} from 'hooks/useOfflineMeasurementInfo';
import { getOfflineQueue } from 'hooks/useOfflineQueue';

const upsertOrderFilePlaceholderInReactQueryCache = (
  orderUuid: string,
  placeholder: OfflineFilePlaceholder & { type?: string; parentUuid?: string }
) => {
  const showPhotosVariants: Array<boolean | undefined> = [undefined, true, false];
  for (const showPhotos of showPhotosVariants) {
    queryClient.setQueryData(
      ['orderDetails', orderUuid, { showPhotos }],
      (oldData: any) => {
        // Jeśli jeszcze nie ma orderDetails w cache (np. ekran wszedł z innego źródła),
        // tworzymy minimalny obiekt wystarczający dla Photos (wystarczy files).
        const base = oldData || { uuid: orderUuid, files: [] };

        // oldData w tym query to OrderType (select w useGetOrderDetails zwraca order)
        const prevFiles = Array.isArray(base.files) ? base.files : [];
        const nextFiles = prevFiles.some((f: any) => f?.uuid === placeholder.uuid)
          ? prevFiles.map((f: any) => (f?.uuid === placeholder.uuid ? { ...f, ...placeholder } : f))
          : [...prevFiles, placeholder];

        return {
          ...base,
          files: nextFiles,
        };
      }
    );
  }
};

interface UploadFileData {
  parentUuid: string;
  type: string;
  files: File[];
}

const OFFLINE_PARENT_KIND_MEASUREMENT_INFO = 'measurementInfo' as const;
const OFFLINE_PARENT_KIND_ORDER = 'order' as const;

// Minimalny placeholder na plik w UI, gdy jesteśmy offline.
// Backend normalnie zwraca pełniejsze obiekty, ale formularz potrzebuje przynajmniej
// listy w measurementInfo.file, żeby „było widać, że coś jest przypięte”.
type OfflineFilePlaceholder = {
  uuid: string;
  name?: string;
  originalName?: string;
  type?: string;
  mimeType?: string;
  size?: number;
  createdAt?: string;
  isOffline?: boolean;
};

const readOfflineApiCache = () => {
  try {
    const raw = localStorage.getItem('offlineApiCache');
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, { data: any; timestamp: number; url: string }>;
  } catch {
    return null;
  }
};

const upsertOrderFilePlaceholderInOfflineApiCache = (
  orderUuid: string,
  placeholder: OfflineFilePlaceholder & { parentUuid?: string; type?: string }
) => {
  try {
    const cache = readOfflineApiCache();
    if (!cache) return;

    const keysToTry = [
      `orders/${orderUuid}`,
      `/orders/${orderUuid}`,
      `orders/${orderUuid}?showPhotos=true`,
      `/orders/${orderUuid}?showPhotos=true`,
    ];

    const now = Date.now();

    for (const k of keysToTry) {
      const entry = cache[k];
      const order = entry?.data?.order;
      if (!order) continue;

      const prev = Array.isArray(order.files) ? order.files : [];
      const next = prev.some((f: any) => f?.uuid === placeholder.uuid)
        ? prev.map((f: any) => (f?.uuid === placeholder.uuid ? { ...f, ...placeholder } : f))
        : [...prev, placeholder];

      cache[k] = {
        ...(entry || { url: k }),
        timestamp: now,
        url: k,
        data: {
          ...entry.data,
          order: {
            ...order,
            files: next,
          },
        },
      };
    }

    localStorage.setItem('offlineApiCache', JSON.stringify(cache));

    // powiadom UI (best-effort)
    try {
      window.dispatchEvent(new Event('offlineApiCacheChanged'));
    } catch {
      // no-op
    }
  } catch (e) {
    console.error('Error updating offlineApiCache for order files:', e);
  }
};

const upsertOfflineMeasurementInfoFile = (measurementUuid: string, patch: OfflineFilePlaceholder) => {
  try {
    const effectiveMeasurementUuid = resolveOfflineUuid(measurementUuid);
    const existing = getOfflineMeasurementInfo(effectiveMeasurementUuid);
    if (!existing) return;

    const prev = Array.isArray(existing.file) ? existing.file : [];
    const next = prev.some((f: any) => f?.uuid === patch.uuid)
      ? prev.map((f: any) => (f?.uuid === patch.uuid ? { ...f, ...patch } : f))
    : [...prev, patch];

    upsertOfflineMeasurementInfo({
      ...existing,
      measurementUuid: effectiveMeasurementUuid,
      file: next,
      updatedAt: new Date().toISOString(),
    });
  } catch (e) {
    console.error('Error updating offline measurement info file list:', e);
  }
};

const getEffectiveFileOperationTempId = (dedupeKey: string, fallbackTempId: string) => {
  try {
    const ops = getOfflineQueue();
    const match = ops
      .filter((op) => op.status === 'pending' || op.status === 'failed')
      .find((op: any) => op?.dedupeKey === dedupeKey);
    return match?.tempId || fallbackTempId;
  } catch {
    return fallbackTempId;
  }
};

const useUploadFile = (
  options?: UseMutationOptions<any, Error, UploadFileData>
) => {
  const [searchParams] = useSearchParams();
  const orderUuid = searchParams.get("orderUuid");
  return useMutation({
    ...options,
    mutationKey: ['uploadFile'],
    mutationFn: async ({ files, parentUuid, type }: UploadFileData) => {
      console.log(parentUuid, 'parentUuid in useUploadFile');
      // Online: multipart/form-data
      // Offline: wrzucamy do kolejki lekki payload bez binarki (żeby nie rozsadzać localStorage).
      // Rysunek i tak trzymamy w sessionStorage jako `canvasState` i odtwarzamy po refresh.
      if (navigator.onLine) {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });

        const params = new URLSearchParams({
          // W ONLINE wysyłamy dokładnie taki parentUuid, jaki dostaliśmy z backendu
          // (measurementInfo.uuid z odpowiedzi PUT /measurement/info).
          parentUuid: parentUuid,
          type: type,
        });
        try {
          const responsePromise = axiosInstance
            .post(`/files?${params.toString()}`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
            .then((res) => res.data);
          const delayPromise = new Promise((resolve) => setTimeout(resolve, 500));
          const [response] = await Promise.all([responsePromise, delayPromise]);

          return response;
        } catch (e: any) {
          // ONLINE: nie wrzucamy do kolejki offline.
          // Jeśli request pada (nawet 503 z SW), ma być błąd w UI, a nie "udane" zakolejkowanie.
          throw e;
        }
      }

      // Offline: rozróżniamy 2 scenariusze:
      // 1) upload rysunku/plików do measurementInfo (parentUuid jest measurementInfo.uuid, często temp)
      // 2) upload zdjęć do orderUuid (parentUuid jest realnym uuid zlecenia) - bez mapowania
      const isOrderUpload = Boolean(orderUuid) && parentUuid === orderUuid;

      if (isOrderUpload) {
        return mutateWithOffline<any>(
          'POST',
          `/files?parentUuid=${encodeURIComponent(parentUuid)}&type=${encodeURIComponent(type)}`,
          {
            parentUuid,
            type,
            offlineParentKind: OFFLINE_PARENT_KIND_ORDER,
            files: files.map((f) => ({
              name: f.name,
              type: f.type,
              size: f.size,
              lastModified: f.lastModified,
            })),
          },
          {
            entityType: 'file',
            // nie dedupe'ujemy po (parentUuid,type), bo user może dodać wiele zdjęć tego samego typu
            simulateResponse: ({ tempId }) => {
              const nowIso = new Date().toISOString();
              const first = files?.[0];
              const effectiveTempId = tempId;

              if (first) {
                setOfflineFileBlob({
                  id: effectiveTempId,
                  name: first.name,
                  type: first.type,
                  blob: first,
                  size: first.size,
                  lastModified: first.lastModified,
                }).catch((e) => {
                  console.error('Error saving offline order file blob:', e);
                });

                // dopisz placeholder do offlineApiCache dla order details,
                // żeby zdjęcie było widoczne po odświeżeniu offline.
                upsertOrderFilePlaceholderInOfflineApiCache(parentUuid, {
                  uuid: effectiveTempId,
                  name: first.name,
                  originalName: first.name,
                  mimeType: first.type,
                  size: first.size,
                  createdAt: nowIso,
                  isOffline: true,
                  parentUuid,
                  type,
                });

                upsertOrderFilePlaceholderInReactQueryCache(parentUuid, {
                  uuid: effectiveTempId,
                  name: first.name,
                  originalName: first.name,
                  mimeType: first.type,
                  size: first.size,
                  createdAt: nowIso,
                  isOffline: true,
                  parentUuid,
                  type,
                });

              }

              return {
                status: 'success',
                message: 'File queued (offline)',
                file: {
                  uuid: effectiveTempId,
                  parentUuid,
                  type,
                  createdAt: nowIso,
                  isOffline: true,
                },
              };
            },
          }
        );
      }

      return mutateWithOffline<any>(
        'POST',
        // UWAGA: parentUuid jest tymczasowy (offline measurementInfo.uuid). Podczas sync
        // zostanie zmapowany na realny uuid measurementInfo.
        `/files?parentUuid=${encodeURIComponent(parentUuid)}&type=${encodeURIComponent(type)}`,
        {
          parentUuid,
          type,
          offlineParentKind: OFFLINE_PARENT_KIND_MEASUREMENT_INFO,
          // zostawiamy listę plików jako metadane (bez binarki)
          // sama binarka trafi do IndexedDB per operationId
          files: files.map((f) => ({
            name: f.name,
            type: f.type,
            size: f.size,
            lastModified: f.lastModified,
          })),
        },
        {
          entityType: 'file',
          dedupeKey: `file:${OFFLINE_PARENT_KIND_MEASUREMENT_INFO}:${parentUuid}:${type}`,
          simulateResponse: ({ tempId }) => {
            const now = new Date().toISOString();

            // WAŻNE: dla file upload używamy dedupeKey (upsert), więc jeśli user edytuje krok
            // i zapisze ponownie BEZ zmiany rysunku, upsert zachowa STARY tempId operacji.
            // mutateWithOffline wygeneruje nowy tempId, ale nie będzie on użyty przez kolejkę.
            // Dlatego musimy użyć "efektywnego" tempId z kolejki.
            const dedupeKey = `file:${OFFLINE_PARENT_KIND_MEASUREMENT_INFO}:${parentUuid}:${type}`;
            const effectiveTempId = getEffectiveFileOperationTempId(dedupeKey, tempId);

            // TempId z mutateWithOffline to nasz identyfikator operacji.
            // Zapisujemy binarkę do IndexedDB pod kluczem tempId.
            // Jeśli user wybrał kilka plików, robimy best-effort: zapisujemy wszystkie w jednym rekordzie
            // (w praktyce dla rysunku mamy 1 plik canvas-image.png).
            // UWAGA: przechowujemy tylko pierwszy plik jako blob (reszta to edge-case).
            const first = files?.[0];
            if (first) {
              // fire-and-forget; kolejka jest już zapisana
              setOfflineFileBlob({
                id: effectiveTempId,
                name: first.name,
                type: first.type,
                blob: first,
                size: first.size,
                lastModified: first.lastModified,
              }).catch((e) => {
                console.error('Error saving offline file blob:', e);
              });

              // Dopisz placeholder do offlineMeasurementInfo.file, żeby po powrocie do edycji plik był widoczny.
              // Kiedy synchronizacja się wykona, backend i tak nadpisze stan w API cache / current-info.
              // Uwaga: parentUuid tutaj to measurementInfo.uuid (temp lub real). Mapowanie temp->real
              // obsługujemy w syncOfflineQueue, ale dla UI offline nie jest to wymagane.
              // Żeby znaleźć measurementUuid, bierzemy go z query stringa (order flow formularzy) jeśli istnieje.
              // Jeśli go nie ma – nic nie robimy, bo nie mamy gdzie dopisać.
              try {
                const currentUrl = window.location.href;
                const u = new URL(currentUrl);
                const measurementUuid = u.searchParams.get('measurementUuid');
                if (measurementUuid) {
                  upsertOfflineMeasurementInfoFile(measurementUuid, {
                    uuid: effectiveTempId,
                    name: first.name,
                    originalName: first.name,
                    mimeType: first.type,
                    size: first.size,
                    createdAt: now,
                    isOffline: true,
                  });
                }
              } catch {
                // no-op
              }
            }

            return {
              status: 'success',
              message: 'File queued (offline)',
              file: {
                uuid: effectiveTempId,
                parentUuid,
                type,
                createdAt: now,
              },
            };
          },
        }
      );
    },
    onMutate: (variables) => {
      Toast({
        message: 'Trwa przetwarzanie pliku.',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'send-file',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      // Jeśli jesteśmy offline, mutateWithOffline już zwróciło placeholder i
      // aktualizujemy cache ręcznie (żeby zdjęcie było widoczne od razu).
      // Invalidate w offline często powoduje "odświeżenie" ze starego cache bez placeholderów.
      if (navigator.onLine) {
        queryClient.invalidateQueries({ queryKey: ['orderDetails', orderUuid] });
      }
      Toast({
        message: 'Plik został zapisany',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'send-file',
      });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Plik nie został zapisany',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'send-file',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useUploadFile;
